import { createFileRoute, lazyRouteComponent } from "@tanstack/react-router";
import { buildHead } from "@/lib/seo";
import { getLocationBySlug } from "@/lib/locationMeta";
import { MATERIALS } from "@/lib/materialMeta";

export const Route = createFileRoute("/_app/locations/$citySlug")({
  head: ({ params }) => {
    const city = getLocationBySlug(params.citySlug);
    if (!city) {
      return buildHead({
        title: "Scrap Yards & Foundry Buyers | LohaLink",
        description: "Browse GST-verified scrap yards and steel foundry buyers across India.",
        canonical: `/locations/${params.citySlug}`,
      });
    }
    const canonical = `https://lohalink.in/locations/${city.slug}`;
    const title = `Verified Scrap Yards & Foundry Buyers in ${city.name} | LohaLink`;
    const description = `Connect directly with top GST-verified scrap yards and steel foundries in ${city.name}. Direct escrow payment, live truck tracking, zero broker fees.`;

    const jsonLdLocalBusiness = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": canonical,
      name: `LohaLink — Scrap Metal Marketplace in ${city.name}`,
      description,
      url: canonical,
      image: "https://lohalink.in/og-image.jpg",
      priceRange: "₹₹",
      areaServed: {
        "@type": "City",
        name: city.name,
        containedInPlace: { "@type": "State", name: city.state },
      },
      address: {
        "@type": "PostalAddress",
        addressLocality: city.name,
        addressRegion: city.state,
        addressCountry: "IN",
      },
      geo: { "@type": "GeoCoordinates", latitude: city.lat, longitude: city.lng },
      knowsAbout: MATERIALS.map((m) => m.name),
      makesOffer: MATERIALS.slice(0, 6).map((m) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Product", name: `${m.name} — ${city.name}` },
        url: `https://lohalink.in/rates/${m.slug}/${city.slug}`,
        priceCurrency: "INR",
      })),
    };

    const jsonLdPlace = {
      "@context": "https://schema.org",
      "@type": "Place",
      name: `${city.name} scrap and steel trading cluster`,
      description: city.blurb,
      address: {
        "@type": "PostalAddress",
        addressLocality: city.name,
        addressRegion: city.state,
        addressCountry: "IN",
      },
      geo: { "@type": "GeoCoordinates", latitude: city.lat, longitude: city.lng },
    };

    const jsonLdBreadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://lohalink.in/" },
        { "@type": "ListItem", position: 2, name: "Locations", item: "https://lohalink.in/locations" },
        { "@type": "ListItem", position: 3, name: city.name, item: canonical },
      ],
    };

    return buildHead({
      title,
      description,
      canonical,
      jsonLd: [jsonLdLocalBusiness, jsonLdPlace, jsonLdBreadcrumb],
    });
  },
  component: lazyRouteComponent(() => import("@/pages/LocationPage")),
});
